import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, asArray } from "@/lib/api";

const empty = {
  slug: "",
  title: "",
  summary: "",
  description: "",
  icon: "Server",
  image_url: "",
  features: [],
  order_idx: 0,
  is_active: true,
};

const inputCls =
  "w-full border border-[#E4E4E7] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0369A1] focus:ring-1 focus:ring-[#0369A1]";

const AdminServices = () => {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null | service object
  const [open, setOpen] = useState(false);

  const load = () =>
    api
      .get("/services", { params: { active_only: false } })
      .then((r) => setList(asArray(r.data)))
      .catch(() => setList([]));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing({ ...empty });
    setOpen(true);
  };
  const openEdit = (s) => {
    setEditing({ ...s });
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setEditing(null);
  };

  const save = async () => {
    try {
      const payload = { ...editing, features: editing.features || [] };
      if (editing.id) {
        await api.put(`/admin/services/${editing.id}`, payload);
        toast.success("Layanan diperbarui");
      } else {
        await api.post("/admin/services", payload);
        toast.success("Layanan dibuat");
      }
      close();
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Gagal menyimpan");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus layanan ini?")) return;
    try {
      await api.delete(`/admin/services/${id}`);
      toast.success("Dihapus");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-services-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="kt-eyebrow">CMS</div>
          <h1 className="font-display text-3xl font-bold mt-2">Layanan</h1>
        </div>
        <button
          onClick={openNew}
          className="kt-btn-primary"
          data-testid="admin-services-add-button"
        >
          <Plus size={16} /> Tambah Layanan
        </button>
      </div>

      <div className="kt-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] border-b border-[#E4E4E7]">
            <tr className="text-left text-xs uppercase tracking-[0.14em] text-[#52525B]">
              <th className="px-4 py-3">Order</th>
              <th className="px-4 py-3">Slug</th>
              <th className="px-4 py-3">Judul</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr
                key={s.id}
                className="border-b border-[#E4E4E7] last:border-0"
                data-testid={`admin-service-row-${s.slug}`}
              >
                <td className="px-4 py-3 font-mono-kt text-[#52525B]">
                  {String(s.order_idx).padStart(2, "0")}
                </td>
                <td className="px-4 py-3 font-mono-kt text-xs">{s.slug}</td>
                <td className="px-4 py-3 font-medium">{s.title}</td>
                <td className="px-4 py-3">
                  <span
                    className={`text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-1 ${
                      s.is_active
                        ? "bg-[#0369A1] text-white"
                        : "bg-[#F4F4F5] text-[#18181B]"
                    }`}
                  >
                    {s.is_active ? "Aktif" : "Nonaktif"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => openEdit(s)}
                    className="p-2 hover:bg-[#F4F4F5]"
                    aria-label="edit"
                    data-testid={`admin-service-edit-${s.id}`}
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => remove(s.id)}
                    className="p-2 hover:bg-[#FEE2E2] text-[#DC2626]"
                    aria-label="delete"
                    data-testid={`admin-service-delete-${s.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && editing && (
        <div
          className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4"
          data-testid="service-editor-modal"
        >
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#E4E4E7]">
              <h2 className="font-display text-xl font-bold">
                {editing.id ? "Edit Layanan" : "Tambah Layanan"}
              </h2>
              <button
                onClick={close}
                className="p-2 hover:bg-[#F4F4F5]"
                data-testid="service-editor-close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Lab label="Slug">
                <input
                  className={inputCls}
                  value={editing.slug}
                  onChange={(e) =>
                    setEditing({ ...editing, slug: e.target.value })
                  }
                  data-testid="service-editor-slug"
                />
              </Lab>
              <Lab label="Judul">
                <input
                  className={inputCls}
                  value={editing.title}
                  onChange={(e) =>
                    setEditing({ ...editing, title: e.target.value })
                  }
                  data-testid="service-editor-title"
                />
              </Lab>
              <Lab label="Ikon (lucide)">
                <input
                  className={inputCls}
                  value={editing.icon}
                  onChange={(e) =>
                    setEditing({ ...editing, icon: e.target.value })
                  }
                  placeholder="Server / MonitorCog / ShieldCheck / Network"
                />
              </Lab>
              <Lab label="Urutan">
                <input
                  type="number"
                  className={inputCls}
                  value={editing.order_idx}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      order_idx: Number(e.target.value),
                    })
                  }
                />
              </Lab>
              <Lab label="URL Gambar" className="md:col-span-2">
                <input
                  className={inputCls}
                  value={editing.image_url}
                  onChange={(e) =>
                    setEditing({ ...editing, image_url: e.target.value })
                  }
                />
              </Lab>
              <Lab label="Ringkasan" className="md:col-span-2">
                <textarea
                  rows={2}
                  className={inputCls}
                  value={editing.summary}
                  onChange={(e) =>
                    setEditing({ ...editing, summary: e.target.value })
                  }
                />
              </Lab>
              <Lab label="Deskripsi" className="md:col-span-2">
                <textarea
                  rows={5}
                  className={inputCls}
                  value={editing.description}
                  onChange={(e) =>
                    setEditing({ ...editing, description: e.target.value })
                  }
                />
              </Lab>
              <Lab label="Features (satu per baris)" className="md:col-span-2">
                <textarea
                  rows={4}
                  className={inputCls}
                  value={(editing.features || []).join("\n")}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      features: e.target.value
                        .split("\n")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </Lab>
              <label className="md:col-span-2 inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editing.is_active}
                  onChange={(e) =>
                    setEditing({ ...editing, is_active: e.target.checked })
                  }
                  data-testid="service-editor-active"
                />
                Aktif (tampil di website)
              </label>
            </div>
            <div className="p-6 border-t border-[#E4E4E7] flex justify-end gap-3">
              <button
                onClick={close}
                className="kt-btn-outline"
                data-testid="service-editor-cancel"
              >
                Batal
              </button>
              <button
                onClick={save}
                className="kt-btn-primary"
                data-testid="service-editor-save"
              >
                Simpan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Lab = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#18181B] mb-1.5">
      {label}
    </span>
    {children}
  </label>
);

export default AdminServices;
